/**
 * Message builder data access. Every read and write goes through the typed
 * `@v0y4ge/api-client` (no raw fetch). This module only names the calls the
 * builder uses and classifies the errors they raise, so the screens can keep
 * the author's input and say exactly what the server refused.
 */
import {
  isApiError,
  messagingRuntime,
  listMessageTemplates,
  getMessageTemplate,
  saveMessageTemplateDraft,
  discardMessageTemplateDraft,
  publishMessageTemplate,
  listMessageTemplateVersions,
  getMessageTemplateVersion,
  getMessageTemplatePreview,
  getMessageTemplateValidation,
  createMessageTemplateLocale,
  listMessagePreviewRecords,
  listMessageLayouts,
  getMessageLayout,
  createMessageLayout,
  saveMessageLayoutDraft,
  publishMessageLayout,
  discardMessageLayoutDraft,
  archiveMessageLayout,
  restoreMessageLayout,
  deleteMessageLayout,
  getMessageLayoutUsage,
  listMessageLayoutVersions,
  getMessageLayoutVersion,
  getMessageLayoutPreview,
  setMessagingDefaultLayout,
} from "@v0y4ge/api-client";
import type {
  MessageBuilderIssue,
  MessageLayoutDetail,
  MessageLayoutSummary,
  MessagingDefaultLayout,
  MessageTemplateDetail,
  MessageTemplateSummary,
} from "@v0y4ge/api-contracts";

export const builderApi = {
  listMessageTemplates,
  getMessageTemplate,
  saveMessageTemplateDraft,
  discardMessageTemplateDraft,
  publishMessageTemplate,
  listMessageTemplateVersions,
  getMessageTemplateVersion,
  getMessageTemplatePreview,
  getMessageTemplateValidation,
  createMessageTemplateLocale,
  listMessagePreviewRecords,
  listMessageLayouts,
  getMessageLayout,
  createMessageLayout,
  saveMessageLayoutDraft,
  publishMessageLayout,
  discardMessageLayoutDraft,
  archiveMessageLayout,
  restoreMessageLayout,
  deleteMessageLayout,
  getMessageLayoutUsage,
  listMessageLayoutVersions,
  getMessageLayoutVersion,
  getMessageLayoutPreview,
  setMessagingDefaultLayout,
  getMessageTokenCatalog: messagingRuntime.getMessageTokenCatalog,
};

export type TemplateSummary = MessageTemplateSummary;
export type TemplateDetail = MessageTemplateDetail;
export type LayoutSummary = MessageLayoutSummary;
export type LayoutDetail = MessageLayoutDetail;
export type DefaultLayout = MessagingDefaultLayout;
export type BuilderIssue = MessageBuilderIssue;
export type TokenCatalog = Awaited<ReturnType<typeof messagingRuntime.getMessageTokenCatalog>>["data"];
export type CatalogToken = TokenCatalog["tokens"][number];
export type TokenContext = TokenCatalog["context"];

/** What a failed call means for the screen. */
export type FailureKind =
  | "conflict"       // someone else changed it first: keep the author's input, offer reload
  | "invalid"        // the server's checks refused it: show its field issues
  | "unsupported"    // this row cannot hold a field: show the server's reason
  | "usage"          // a layout is in use / the default: show who uses it
  | "selection"      // the default layout choice was refused
  | "forbidden"      // no permission
  | "notFound"
  | "idempotency"    // the same intent key was reused for a different request
  | "transient"      // service warming or network: retry the SAME intent
  | "failed";

export interface Failure {
  kind: FailureKind;
  code: string;
  status: number;
  message: string;
  fields: { path: string; code: string; message: string }[];
  params: Record<string, unknown>;
}

/** Someone else moved the row first (or the saved draft is gone): reload it, keep the author's input. */
const CONFLICT_CODES = new Set(["MESSAGE_TEMPLATE_REVISION_CONFLICT", "MESSAGE_LAYOUT_REVISION_CONFLICT", "MESSAGE_LAYOUT_SELECTION_CONFLICT", "MESSAGE_LAYOUT_DRAFT_UNAVAILABLE"]);
/** Refused because of the messages that depend on a layout: show the server's explanation, nothing is retargeted. */
const USAGE_CODES = new Set(["MESSAGE_LAYOUT_USAGE_CONFLICT", "MESSAGE_LAYOUT_SECTION_DEPENDENCY_CONFLICT"]);

export function classifyFailure(error: unknown): Failure {
  if (!isApiError(error)) {
    const message = error instanceof Error && error.message ? error.message : "";
    return { kind: "failed", code: "", status: 0, message, fields: [], params: {} };
  }
  const code = error.code || "";
  const base = {
    code,
    status: error.status,
    message: error.message || "",
    fields: (error.fields || []).map((f) => ({ path: String(f.path ?? ""), code: String(f.code ?? ""), message: String(f.message ?? "") })),
    params: (error.params || {}) as Record<string, unknown>,
  };
  if (CONFLICT_CODES.has(code)) return { kind: "conflict", ...base };
  // A refusal with the server's own reason (an unpublished or archived layout included): show it, nothing to reload.
  if (code === "MESSAGE_TEMPLATE_INVALID" || code === "MESSAGE_LAYOUT_INVALID" || code === "MESSAGE_LAYOUT_UNAVAILABLE") return { kind: "invalid", ...base };
  if (code === "MESSAGE_TEMPLATE_UNSUPPORTED") return { kind: "unsupported", ...base };
  if (USAGE_CODES.has(code)) return { kind: "usage", ...base };
  if (code === "MESSAGE_LAYOUT_SELECTION_INVALID") return { kind: "selection", ...base };
  if (code === "IDEMPOTENCY_KEY_CONFLICT") return { kind: "idempotency", ...base };
  if (error.status === 403) return { kind: "forbidden", ...base };
  if (error.status === 404) return { kind: "notFound", ...base };
  if (error.transient || error.status === 0) return { kind: "transient", ...base };
  // Only the codes above are "someone else changed this first": any other 409 is shown as the server explains it.
  return { kind: "failed", ...base };
}

/** Field issues from a refused write, in the same shape the validation response uses. */
export function failureIssues(f: Failure): BuilderIssue[] {
  return f.fields.map((x) => ({ path: x.path || "content", code: x.code || "invalid", message: x.message || f.message }));
}
